/**
 * Quoted Book insight charts: likelihood band donut (count / book £ /
 * expected £ toggle) and quote-age histogram benchmarked against the
 * median time-to-win from the Commissioned Book.
 */
import { useState } from "react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip as RechartsTooltip,
} from "recharts";
import { PanelTitle, fmtGbpShort } from "./CommissionedPanels";

const BAND_ORDER = ["hot", "warm", "cool", "cold"];
const BAND_COLORS = { hot: "#0E9F6E", warm: "#3EA8DC", cool: "#F59E0B", cold: "#A8A29E" };
const BAND_LABELS = { hot: "Hot", warm: "Warm", cool: "Cool", cold: "Cold" };
const METRICS = [
  { key: "count", label: "quotes" },
  { key: "book", label: "book £" },
  { key: "expected", label: "expected £" },
];

export function BandDonut({ items }) {
  const [metric, setMetric] = useState("count");
  const data = BAND_ORDER.map((b) => {
    const rows = items.filter((i) => i.band === b);
    return {
      name: BAND_LABELS[b],
      count: rows.length,
      book: rows.reduce((s, i) => s + (i.value_gbp || 0), 0),
      expected: rows.reduce((s, i) => s + (i.expected_value || 0), 0),
      fill: BAND_COLORS[b],
    };
  }).filter((d) => d.count > 0)
    .map((d) => ({ ...d, value: metric === "count" ? d.count : d[metric] }));
  return (
    <div data-testid="quoted-band-donut" className="bg-white rounded-xl border border-stone-200 p-4">
      <div className="flex items-center justify-between mb-1">
        <PanelTitle>Likelihood bands</PanelTitle>
        <div className="flex rounded-md border border-stone-200 overflow-hidden text-[10px] font-mono uppercase">
          {METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMetric(m.key)}
              data-testid={`quoted-band-toggle-${m.key}`}
              className={`px-2 py-1 transition-colors ${metric === m.key ? "bg-[#1B4F8A] text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data} dataKey="value" nameKey="name"
              cx="50%" cy="50%" innerRadius={38} outerRadius={64}
              paddingAngle={2} strokeWidth={1}
            >
              {data.map((d) => <Cell key={d.name} fill={d.fill} />)}
            </Pie>
            <RechartsTooltip
              formatter={(_v, name, p) => [
                `${p.payload.count} quote${p.payload.count === 1 ? "" : "s"} · book ${fmtGbpShort(p.payload.book)} · expected ${fmtGbpShort(p.payload.expected)}`,
                name,
              ]}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-2 space-y-1" data-testid="quoted-band-legend">
        {data.map((d) => (
          <li key={d.name} className="flex items-center gap-2 text-xs text-stone-600">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.fill }} />
            <span className="font-medium text-slate-800">{d.name}</span>
            <span className="ml-auto font-mono tabular-nums">
              {d.count} · {fmtGbpShort(metric === "expected" ? d.expected : d.book)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const AGE_BUCKETS = [
  { label: "0-30d", min: 0, max: 30, fill: "#3EA8DC" },
  { label: "31-60d", min: 31, max: 60, fill: "#8CC8E8" },
  { label: "61-90d", min: 61, max: 90, fill: "#F59E0B" },
  { label: "90d+", min: 91, max: Infinity, fill: "#F43F5E" },
];

export function AgeHistogram({ items, medianWinDays }) {
  const data = AGE_BUCKETS.map((b) => {
    const rows = items.filter(
      (i) => i.age_days != null && i.age_days >= b.min && i.age_days <= b.max
    );
    return {
      label: b.label,
      count: rows.length,
      book: rows.reduce((s, i) => s + (i.value_gbp || 0), 0),
      fill: b.fill,
    };
  });
  const stale = items.filter((i) => (i.age_days || 0) > 90);
  const staleBook = stale.reduce((s, i) => s + (i.value_gbp || 0), 0);
  return (
    <div data-testid="quoted-age-histogram" className="bg-white rounded-xl border border-stone-200 p-4">
      <PanelTitle>Quote age vs win window</PanelTitle>
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, left: -26, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
            <RechartsTooltip
              formatter={(v, _n, p) => [`${v} quote${v === 1 ? "" : "s"} · ${fmtGbpShort(p.payload.book)} book value`, p.payload.label]}
              labelFormatter={() => ""}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
            />
            <Bar dataKey="count" radius={[3, 3, 0, 0]} maxBarSize={40}>
              {data.map((d) => <Cell key={d.label} fill={d.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-stone-500 mt-2 leading-relaxed" data-testid="quoted-age-stale-note">
        {medianWinDays != null && (
          <>Wins typically land within <span className="font-semibold text-[#1B4F8A]">{Math.round(medianWinDays)} days</span> of quoting. </>
        )}
        <span className={stale.length ? "text-rose-700 font-medium" : ""}>
          {stale.length
            ? `${stale.length} quote${stale.length === 1 ? "" : "s"} (${fmtGbpShort(staleBook)}) have been open 90d+.`
            : "No quotes older than 90 days."}
        </span>
      </p>
    </div>
  );
}
